/* eslint-disable react/prop-types */
import { AlertTriangle, Plus, RotateCcw, Save, Send, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import { discardCmsDraft, fetchCmsContent, fetchCmsVersions, formatDateTime, publishCmsContent, rollbackCmsContent, saveCmsDraft } from '../lib/cms'
import { validateCmsDraft } from '../../../../packages/validation/cms'
import { EMPTY_CAFETERIA_COPY, EMPTY_CAFETERIA_OVERRIDE_ITEM, EMPTY_NOTICE_OVERRIDE_ITEM } from '../../../../packages/types/cms'

const TABS = ['cafeteria', 'notice']
const TAB_LABELS = { cafeteria: '학식', notice: '학교 공지' }

function newId(prefix) { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }

function OverrideEditor({ contentType, emptyItem, renderFields, renderCopy, itemLabel, rowSummary, canEdit, uid, sourceLabel, sourceLoader }) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [items, setItems] = useState([])
  const [copy, setCopy] = useState({})
  const [versions, setVersions] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [source, setSource] = useState(null)
  const savedSnapshotRef = useRef('[]')

  async function load() {
    setLoading(true); setError('')
    try {
      const { production: prod, draft: dr } = await fetchCmsContent(contentType)
      setProduction(prod); setDraft(dr)
      const stored = dr?.data?.items ?? prod?.data?.items ?? []
      const initial = contentType === 'cafeteriaOverrides' ? stored.filter(item => ['lecture', 'staff'].includes(item.restaurantId)) : stored
      setItems(initial)
      setCopy(dr?.data?.copy ?? prod?.data?.copy ?? {})
      savedSnapshotRef.current = JSON.stringify({ items: initial, copy: dr?.data?.copy ?? prod?.data?.copy ?? {} })
      setVersions(await fetchCmsVersions(contentType))
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- contentType 변경 시에만 다시 불러온다.
  useEffect(() => { load() }, [contentType])
  useEffect(() => { sourceLoader().then(setSource).catch(() => setSource(null)) }, [sourceLoader])

  const dirty = JSON.stringify({ items, copy }) !== savedSnapshotRef.current
  const validation = useMemo(() => validateCmsDraft(contentType, { items, copy }), [contentType, items, copy])
  const selected = items.find(item => item.id === selectedId) || null

  function updateSelected(patch) { setItems(current => current.map(item => item.id === selectedId ? { ...item, ...patch } : item)) }
  function addItem() {
    const item = { ...emptyItem, id: newId(contentType) }
    setItems(current => [...current, item])
    setSelectedId(item.id)
  }
  function removeItem(id) { setItems(current => current.filter(item => item.id !== id)); if (selectedId === id) setSelectedId(null) }
  function toggleActive(item) { setItems(current => current.map(entry => entry.id === item.id ? { ...entry, active: !(entry.active !== false) } : entry)) }

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCmsDraft(contentType, { items, copy }, { uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify({ items, copy })
      const { draft: fresh } = await fetchCmsContent(contentType)
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCmsDraft(contentType, uid); await load(); setMessage('초안을 버렸습니다.') } catch (err) { setError(err.message) } finally { setBusy(false) }
  }
  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 배포할 수 없습니다.'); return }
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCmsContent(contentType, renderCopy ? { items, copy } : { items }, { uid })
      await load()
      setMessage(`v${version}으로 배포했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }
  async function rollback(version) {
    if (!window.confirm(`v${version}으로 롤백할까요?`)) return
    setBusy(true); setMessage(''); setError('')
    try { const next = await rollbackCmsContent(contentType, version, { uid }); await load(); setMessage(`v${version} → v${next}로 롤백했습니다.`) } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  if (loading) return <div className="empty-state">{itemLabel} override를 불러오는 중…</div>
  return (
    <>
      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">production v{production?.version ?? 0}{draft ? ` · 초안 저장 ${formatDateTime(draft.savedAt)}` : ' · 초안 없음'}</span>
            <span className="muted">{sourceLabel}: {source ? '불러옴' : '불러오는 중/실패'}</span>
            {validation.errors.length > 0 && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 200 }} disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {validation.errors.length > 0 && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--danger)' }}>
            {validation.errors.slice(0, 8).map((entry, idx) => <li key={idx}>{entry.id ? `[${entry.id}] ` : ''}{entry.field}: {entry.message}</li>)}
          </ul>
        )}
      </section>
      {error && <div className="error-state" style={{ marginBottom: 16 }}>{error}</div>}
      {message && <div className="temporary-warning" style={{ marginBottom: 16 }}>{message}</div>}
      {renderCopy && <section className="panel" style={{ padding: 16, marginBottom: 16 }}><h2 style={{ marginTop: 0 }}>앱 안내 문구</h2>{renderCopy(copy, setCopy, canEdit)}</section>}

      <section className={`panel split-view ${selected ? 'has-selection' : ''}`}>
        <div className="split-list">
          {canEdit && <div className="toolbar"><button className="button secondary" type="button" onClick={addItem}><Plus size={16} />override 추가</button></div>}
          <div className="split-list-items">
            {items.length === 0 ? <div className="empty-state">override가 없습니다. 크롤러 원본이 그대로 앱에 노출됩니다.</div> : items.map(item => (
              <button type="button" key={item.id} className={`split-item ${item.id === selectedId ? 'active' : ''}`} onClick={() => setSelectedId(item.id)}>
                <div className="split-item-top">
                  <strong>{rowSummary(item)}</strong>
                  <span className={`status-badge ${item.active === false ? 'ended' : 'published'}`}>{item.active === false ? '비활성' : '활성'}</span>
                </div>
                <p>{item.id}</p>
              </button>
            ))}
          </div>
        </div>
        <article className="detail-panel">
          {!selected ? <div className="empty-state">왼쪽 목록에서 override를 선택하거나 새로 만드세요.</div> : (
            <div className="form-section">
              <h2>{itemLabel} override</h2>
              {renderFields(selected, updateSelected, canEdit, source)}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button type="button" className="button secondary" onClick={() => toggleActive(selected)}>{selected.active === false ? '활성화' : '비활성화(해제)'}</button>
                  <button type="button" className="button danger" onClick={() => removeItem(selected.id)}><Trash2 size={14} />삭제</button>
                </div>
              )}
            </div>
          )}
        </article>
      </section>
    </>
  )
}

function CafeteriaFields(item, set, canEdit, source) {
  const activeRestaurants = source?.restaurants?.filter(restaurant => ['lecture', 'staff'].includes(restaurant.id)) ?? []
  const restaurantOptions = activeRestaurants.length ? activeRestaurants : [{ id: 'lecture', name: '종합강의동 학생식당' }, { id: 'staff', name: '교직원식당' }]
  const originalDay = restaurantOptions.find(r => r.id === item.restaurantId)?.weeklyMenu?.find(d => d.date === item.date)
  return (
    <div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>식당</label>
        <select className="select" disabled={!canEdit} value={item.restaurantId} onChange={e => set({ restaurantId: e.target.value })}>
          <option value="">선택…</option>{restaurantOptions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <div className="field"><label>날짜</label><input className="input" type="date" disabled={!canEdit} value={item.date} onChange={e => set({ date: e.target.value })} /></div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.closed === true} onChange={e => set({ closed: e.target.checked })} />휴무(closed)</label></div>
      {!item.closed && (
        <>
          <div className="field full"><label>아침 (쉼표로 구분, 비우면 메뉴 없음)</label><input className="input" disabled={!canEdit} value={(item.breakfast ?? []).join(', ')} onChange={e => set({ breakfast: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} /></div>
          <div className="field full"><label>점심 (쉼표로 구분, 비우면 메뉴 없음)</label><input className="input" disabled={!canEdit} value={(item.lunch ?? []).join(', ')} onChange={e => set({ lunch: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} /></div>
          <div className="field full"><label>저녁 (쉼표로 구분, 비우면 메뉴 없음)</label><input className="input" disabled={!canEdit} value={(item.dinner ?? []).join(', ')} onChange={e => set({ dinner: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} /></div>
        </>
      )}
      <div className="field full"><label>메모</label><input className="input" disabled={!canEdit} value={item.note ?? ''} onChange={e => set({ note: e.target.value })} /></div>
      {originalDay && (
        <div className="field full"><span className="label">크롤러 원본 (비교용)</span>
          <div className="privacy-block">아침 {originalDay.breakfast?.join(', ') || '-'} · 점심 {originalDay.lunch?.join(', ') || '-'} · 저녁 {originalDay.dinner?.join(', ') || '-'}</div>
        </div>
      )}
    </div>
  )
}

function CafeteriaCopyFields(copy, set, canEdit) {
  const value = { ...EMPTY_CAFETERIA_COPY, ...copy }
  const field = (key, label, multiline = false) => <div className="field"><label>{label}</label>{multiline ? <textarea className="input" rows={2} disabled={!canEdit} value={value[key]} onChange={e => set(current => ({ ...current, [key]: e.target.value }))} /> : <input className="input" disabled={!canEdit} value={value[key]} onChange={e => set(current => ({ ...current, [key]: e.target.value }))} />}</div>
  return <div className="field-grid">{field('mealEmptyTitle', '오늘 메뉴 없음 제목')}{field('mealEmptyDescription', '오늘 메뉴 없음 설명', true)}{field('weeklyEmptyTitle', '주간 메뉴 없음 제목')}{field('weeklyEmptyDescription', '주간 메뉴 없음 설명', true)}{field('dataErrorMessage', '데이터 오류 안내', true)}</div>
}

function NoticeFields(item, set, canEdit, source) {
  const isAuthored = !item.sourceNoticeId
  const originalNotice = (source?.items ?? []).find(n => String(n.id) === String(item.sourceNoticeId))
  return (
    <div className="field-grid">
      <div className="field"><label>ID</label><input className="input" disabled={!canEdit} value={item.id} onChange={e => set({ id: e.target.value })} /></div>
      <div className="field"><label>원본 공지 ID (비우면 직접 작성)</label>
        <select className="select" disabled={!canEdit} value={item.sourceNoticeId ?? ''} onChange={e => set({ sourceNoticeId: e.target.value })}>
          <option value="">(직접 작성)</option>{(source?.items ?? []).map(n => <option key={n.id} value={n.id}>{n.title}</option>)}
        </select>
      </div>
      <div className="field"><label>제목{isAuthored ? '' : ' (비우면 원본 유지)'}</label><input className="input" disabled={!canEdit} value={item.title ?? ''} onChange={e => set({ title: e.target.value })} /></div>
      <div className="field"><label>카테고리</label><input className="input" disabled={!canEdit} value={item.category ?? ''} onChange={e => set({ category: e.target.value })} /></div>
      <div className="field"><label>게시일</label><input className="input" type="date" disabled={!canEdit} value={item.date ?? ''} onChange={e => set({ date: e.target.value })} /></div>
      <div className="field"><label>원문 URL (https://)</label><input className="input" disabled={!canEdit} value={item.url ?? ''} onChange={e => set({ url: e.target.value })} /></div>
      <div className="field full"><label>요약</label><input className="input" disabled={!canEdit} value={item.summary ?? ''} onChange={e => set({ summary: e.target.value })} /></div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.important === true} onChange={e => set({ important: e.target.checked })} />중요 공지</label></div>
      <div className="field"><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={item.visible !== false} onChange={e => set({ visible: e.target.checked })} />앱 노출</label></div>
      {originalNotice && (
        <div className="field full"><span className="label">크롤러 원본 (비교용)</span>
          <div className="privacy-block">{originalNotice.title} · {originalNotice.category} · {originalNotice.date}</div>
        </div>
      )}
    </div>
  )
}

async function loadCafeteriaSource() {
  const res = await fetch('https://suguide.pages.dev/cafeteriaData.json', { cache: 'no-store' })
  if (!res.ok) throw new Error('학식 원본을 불러오지 못했습니다.')
  return res.json()
}
async function loadNoticeSource() {
  const res = await fetch('/notices.json', { cache: 'no-store' })
  if (!res.ok) throw new Error('공지 원본을 불러오지 못했습니다.')
  const data = await res.json()
  return { items: Array.isArray(data) ? data : data?.items ?? [] }
}

export default function CafeteriaNoticePage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [tab, setTab] = useState('cafeteria')

  return (
    <>
      <header className="page-header"><div><p className="eyebrow">콘텐츠</p><h1>학식·학교 공지</h1><p>원본 데이터는 유지하고, 필요한 수정 사항만 덧붙여 배포합니다. 원본 수집이 다시 실행돼도 수정 내용은 유지됩니다.</p></div></header>
      <div className="toolbar">
        {TABS.map(t => <button key={t} type="button" className={`check-chip ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{TAB_LABELS[t]}</button>)}
      </div>
      {tab === 'cafeteria' && (
        <OverrideEditor
          contentType="cafeteriaOverrides" emptyItem={EMPTY_CAFETERIA_OVERRIDE_ITEM} renderFields={CafeteriaFields} renderCopy={CafeteriaCopyFields}
          itemLabel="학식" rowSummary={item => `${item.restaurantId || '(식당 미지정)'} · ${item.date || '(날짜 미지정)'}`}
          canEdit={canEdit} uid={auth.user.uid} sourceLabel="크롤러 원본" sourceLoader={loadCafeteriaSource}
        />
      )}
      {tab === 'notice' && (
        <OverrideEditor
          contentType="noticeOverrides" emptyItem={EMPTY_NOTICE_OVERRIDE_ITEM} renderFields={NoticeFields}
          itemLabel="공지" rowSummary={item => item.title || (item.sourceNoticeId ? `원본 #${item.sourceNoticeId} 수정` : '(제목 없음)')}
          canEdit={canEdit} uid={auth.user.uid} sourceLabel="크롤러 원본" sourceLoader={loadNoticeSource}
        />
      )}
    </>
  )
}
